/**
 * @file src/helpers/user.ts
 * @exports canViewFullDobForRole
 * @exports canViewOrganizationIdForRole
 * @exports formatDob
 * @exports hasCraAdminRole
 * @exports hasSuperAdminRole
 * @summary Role predicates for the current user: org-level admin checks, organization ID and date-of-birth visibility.
 */
import { type MeQuery, RoleEnum } from "src/generated/graphql";
import { UserRoleEnum } from "src/types";

export const hasSuperAdminRole = (user: MeQuery["me"]) => {
	const isSuperAdmin = user?.roleAssignment?.some((ra) => ra.roleV2.name === UserRoleEnum.SuperAdmin);
	return !!isSuperAdmin;
};

export const hasCraAdminRole = (user: MeQuery["me"]) => {
	const isCraAdmin = user?.roleAssignment?.some((ra) => ra.roleV2.name === UserRoleEnum.CraAdmin);
	return !!isCraAdmin;
};

/** CRA-side roles need organization IDs for operational and integration work; org-side roles don't see them. */
export const canViewOrganizationIdForRole = ({ role }: { role?: string | null }) =>
	Boolean(
		role &&
			[
				UserRoleEnum.CerebrumTeam,
				UserRoleEnum.CraAdmin,
				UserRoleEnum.IntegrationAdmin,
				UserRoleEnum.Processor,
				UserRoleEnum.SuperAdmin,
			].includes(role as UserRoleEnum),
	);

/** CRA-side roles already see the full date of birth on reports. Everyone else sees it without the year. */
export const canViewFullDobForRole = ({ role }: { role?: string | null }) =>
	Boolean(
		role &&
			[
				RoleEnum.CerebrumTeam,
				RoleEnum.CraAdmin,
				RoleEnum.LimitedProcessor,
				RoleEnum.Processor,
				RoleEnum.SuperAdmin,
			].includes(role as RoleEnum),
	);

/** Formats a stored date of birth as MM/DD/YYYY, or MM/DD/•••• when masked. */
export const formatDob = ({ dob, masked }: { dob?: string; masked: boolean }) => {
	const [, year, month, day] = dob?.match(/^(\d{4})-(\d{2})-(\d{2})/) ?? [];
	const [, usMonth, usDay, usYear] = dob?.match(/^(\d{2})\/(\d{2})\/(\d{4})$/) ?? [];
	if (!dob || !(year || usYear)) {
		return masked && dob ? "••/••/••••" : dob;
	}
	return `${month ?? usMonth}/${day ?? usDay}/${masked ? "••••" : (year ?? usYear)}`;
};
